/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosResponse } from 'axios';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { modelTrainingStatusAnalyzer } from 'src/core/utils/trainingHelpers';
import { TrainingGetStatusSwagger } from 'src/luis/core/services/training/trainingSwagger';
import { modelTrainingStatusSwaggerToModelTrainingStatus } from 'src/luis/modules/training/trainingMappers';

/**
 * Checks if the long running call should retry with another call
 * or it is already finished either by success or failure.
 *
 * @param response The response of the last call to determine the status from.
 */
const defaultRetryStrategy = <T extends OperationStatusBaseSwagger>(response: AxiosResponse<T>): boolean =>
    response.data.status !== 'succeeded' && response.data.status !== 'failed';

/**
 * Checks if the `training` long running call should retry with another call
 * or it is already finished either by success or failure.
 *
 * @param response The response of the last call to determine the status from.
 * @returns True if the call should be retried and false if it shouldn't be called again.
 */
const trainingRetryStrategy = (response: AxiosResponse<TrainingGetStatusSwagger>): boolean => {
    const modelsData = modelTrainingStatusSwaggerToModelTrainingStatus(response.data);

    const failedModel = modelTrainingStatusAnalyzer.getFailedModel(modelsData);

    if (
        failedModel !== undefined ||
        modelTrainingStatusAnalyzer.isUpToDate(modelsData) ||
        modelTrainingStatusAnalyzer.isCompleted(modelsData)
    ) {
        return false;
    }

    return true;
};

/**
 * An object that has the possible retry strategies.
 */
export const OperationRetryStrategies = { trainingRetryStrategy, defaultRetryStrategy };
